import { message } from 'antd'
import { requestGetEventById } from 'app/api/event'
import { DefineTypeEventEnum } from 'app/api/event/constant'
import { IEvent, TypeEventEnum } from 'app/api/event/model'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { SectionBase } from 'app/components/common/SectionBase'
import HeaderPage from 'app/components/header/components/HeaderPage'
import { FORMAT_DD_MM_YYYY_HH_MM } from 'app/constant'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { handleDownloadExcel } from 'app/service/general/service'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { isEqual } from 'lodash'
import { BORDER_RADIUS, DateUtil, PADDING, ResponseType } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import { RenderText } from '../hook'
import { ExpandedRowRender } from '../layouts'
import { StatusEventWrapper } from '../styles'
import { DefineEventStatus, EventStatusEnum } from '../type'
import { ListMemberPrizeLayout, ListTeamPrizeLayout } from './layouts'
import { ModalUpdate } from './modal'
import { Loading } from 'app/components/common/Loading'

const DetailEventContainer = () => {
  const route = useLocation()
  const event: IEvent = useMemo(() => {
    return route?.state?.event
  }, [route])
  const { t } = useTranslation()
  const { user, verifyPermission } = useVerifyPermission()
  const [isLoading, setIsLoading] = useState(false)
  const [eventData, setEventData] = useState<IEvent>(event)

  const isHavePermissionUpdateEvent = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateEvent)
  }, [user])



  const getDetailEvent = async () => {
    try {
      if (!event?.id) return
      setIsLoading(true)

      const res: ResponseType<IEvent> = await requestGetEventById(event?.id)
      if (res) {
        setEventData(res?.data)
      }
    } catch (error) {
      console.log({ error })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!event) {
      message.error('Không tìm thấy sự kiện')
    }

    getDetailEvent()
  }, [event])

  const downloadExcel = async (): Promise<void> => {
    if (!event?.id) return

    await handleDownloadExcel({
      params: {
        id: event?.id ?? 0,
        eventId: event?.id ?? 0,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}admin/activity/download`,
    })
  }

  return (
    <RootWrapper size={0}>
      <HeaderPage
        title={`Chi tiết sự kiện "${eventData?.name_event}"`}
        isExport={verifyPermission(PermissionEnum.ExportEvent)}
        onPressExport={downloadExcel}
      />

      <BaseSpace>
        <SectionBase
          title="Thông tin sự kiện"
          extra={
            isHavePermissionUpdateEvent ? <ModalUpdate event={eventData} /> : null
          }
        >
          <BaseSpace>
            <BaseRow gutter={[16, 16]} align={'top'} justify={'start'}>
              <BaseCol xl={8}>
                <BaseSpace size={4}>
                  <BaseText
                    children={'Tên sự kiện'}
                    fontWeight="medium"
                    fontSize="xs"
                    opacity="0.7"
                  />
                  <BaseText
                    children={eventData?.name_event ?? '-'}
                    fontWeight="semibold"
                    fontSize="xs"
                  />
                </BaseSpace>
              </BaseCol>
              <BaseCol xl={8}>
                <BaseSpace size={4}>
                  <BaseText
                    children={'Loại sự kiện'}
                    fontWeight="medium"
                    fontSize="xs"
                    opacity="0.7"
                  />
                  <BaseText
                    children={
                      eventData?.type ? DefineTypeEventEnum[eventData?.type]?.text : '-'
                    }
                    fontWeight="semibold"
                    colorText="statesGreenColor"
                    fontSize="xs"
                  />
                </BaseSpace>
              </BaseCol>
              {eventData?.link ? (
                <BaseCol xl={8}>
                  <BaseSpace size={8}>
                    <BaseText
                      children={'Link bài viết'}
                      fontWeight="medium"
                      fontSize="xs"
                      opacity="0.7"
                    />
                    <a href={eventData?.link} target="_blank">
                      <BaseText
                        children={eventData?.link}
                        fontWeight="medium"
                        fontSize="xs"
                        colorText="collapseBackgroundColor"
                      />
                    </a>
                  </BaseSpace>
                </BaseCol>
              ) : null}
            </BaseRow>
            <BaseRow gutter={[20, 20]} align={'top'} justify={'start'}>
              {isEqual(eventData?.type, TypeEventEnum.NEWS) ||
              isEqual(eventData?.type, TypeEventEnum.CARE) ? null : (
                <BaseCol xl={8}>
                  <BaseSpace size={4}>
                    <BaseText
                      children={'Thời gian đăng ký'}
                      fontWeight="medium"
                      fontSize="xs"
                      opacity="0.7"
                    />
                    <BaseText
                      children={`${DateUtil?.formatDate(
                        eventData?.time_register_start ?? '',
                        FORMAT_DD_MM_YYYY_HH_MM,
                      )} - ${DateUtil?.formatDate(
                        eventData?.time_register_end ?? '',
                        FORMAT_DD_MM_YYYY_HH_MM,
                      )}`}
                      fontWeight="semibold"
                      fontSize="xs"
                    />
                  </BaseSpace>
                </BaseCol>
              )}

              {isEqual(eventData?.type, TypeEventEnum.NEWS) ? null : (
                <BaseCol xl={8}>
                  <BaseSpace size={4}>
                    <BaseText
                      children={'Thời gian diễn ra sự kiện'}
                      fontWeight="medium"
                      fontSize="xs"
                      opacity="0.7"
                    />
                    <BaseText
                      children={`${DateUtil?.formatDate(
                        eventData?.time_start ?? '',
                        FORMAT_DD_MM_YYYY_HH_MM,
                      )} - ${DateUtil?.formatDate(
                        eventData?.time_end ?? '',
                        FORMAT_DD_MM_YYYY_HH_MM,
                      )}`}
                      fontWeight="semibold"
                      fontSize="xs"
                    />
                  </BaseSpace>
                </BaseCol>
              )}

              {eventData?.status ? (
                <BaseCol xl={8}>
                  <BaseSpace size={4}>
                    <BaseText
                      children={'Trạng thái sự kiện'}
                      fontWeight="medium"
                      fontSize="xs"
                      opacity="0.7"
                    />
                    <StatusEventWrapper
                      $status={(() => {
                        return eventData?.status
                      })()}
                    >
                      <BaseText
                        children={(() => {
                          return t(DefineEventStatus[eventData?.status]?.keyI18n)
                        })()}
                        colorText={(() => {
                          switch (eventData?.status) {
                            case EventStatusEnum.NOT_OPEN:
                              return 'otherBlueColor'
                            case EventStatusEnum.REGISTRATION:
                              return 'otherBlueColor'
                            case EventStatusEnum.REGISTRATION_COMPLETED:
                              return 'statesOrangeColor'
                            case EventStatusEnum.IN_PROGRESS:
                              return 'statesGreenColor'
                            case EventStatusEnum.FINISHED:
                              return 'statesRedColor'
                          }
                        })()}
                        fontWeight="medium"
                      />
                    </StatusEventWrapper>
                  </BaseSpace>
                </BaseCol>
              ) : null}
            </BaseRow>

            <BaseSpace size={4}>
              <BaseText
                children={'Thông tin sự kiện'}
                fontWeight="medium"
                fontSize="xs"
                opacity="0.7"
              />
              <BaseText
                children={<RenderText text={eventData?.description ?? ''} />}
                fontWeight="medium"
                fontSize="xs"
              />
            </BaseSpace>
            {isLoading ? <Loading /> : null}
          </BaseSpace>
        </SectionBase>

        {!isEqual(eventData?.type, TypeEventEnum.NEWS) ? (
          <ExpandedRowRender event={event} />
        ) : null}

        {isEqual(eventData?.type, TypeEventEnum.ACTIVITY) ? (
          <SectionBase title="Thống kê">
            <BaseSpace>
              <ListMemberPrizeLayout event={event} />

              <ListTeamPrizeLayout event={event} />
            </BaseSpace>
          </SectionBase>
        ) : null}
      </BaseSpace>
    </RootWrapper>
  )
}

export default DetailEventContainer

const RootWrapper = styled(BaseSpace)`
  padding: 0px ${PADDING.md};

  .button-background {
    background-color: ${convertedVariables.backgroundColor};
    border-radius: ${BORDER_RADIUS};
  }
`
